import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useTranslation } from "../../../hooks/useTranslation";
import { LEGAL_DOCUMENTS, LegalBlock, LegalDocumentKey } from "./legalTexts";
import "./styles.scss";

const TABS: { key: LegalDocumentKey; label: string }[] = [
  { key: "privacy", label: "tab_privacy" },
  { key: "terms", label: "tab_terms" },
  { key: "dpa", label: "tab_dpa" },
];

// The documents' own names, in the documents' language (English), so they read
// correctly inside the left-to-right article even when the app is in Hebrew.
const DOC_HEADINGS: Record<LegalDocumentKey, { title: string; note?: string }> = {
  privacy: { title: "Privacy Policy" },
  terms: { title: "Terms of Service" },
  dpa: { title: "Data Processing Addendum (DPA)", note: "For EU/EEA & UK organizations" },
};

// Consecutive list items render as one <ul>; everything else is a heading or paragraph.
const groupBlocks = (blocks: LegalBlock[]) => {
  const groups: (LegalBlock | LegalBlock[])[] = [];
  blocks.forEach((block) => {
    const last = groups[groups.length - 1];
    if (block.type === "li" && Array.isArray(last)) last.push(block);
    else groups.push(block.type === "li" ? [block] : block);
  });
  return groups;
};

interface Props {
  open: boolean;
  onClose: () => void;
  initialTab?: LegalDocumentKey;
}

export const LegalDocumentsDialog: React.FC<Props> = ({ open, onClose, initialTab = "privacy" }) => {
  const { p, i18n } = useTranslation("legal_docs");
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const [tab, setTab] = useState<LegalDocumentKey>(initialTab);
  const contentRef = useRef<HTMLDivElement>(null);

  // A new document starts at its top, not where the previous one was scrolled to.
  useEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [tab]);

  const doc = LEGAL_DOCUMENTS[tab];
  const heading = DOC_HEADINGS[tab];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isPhone}
      fullWidth
      maxWidth="md"
      scroll="paper"
      aria-labelledby="legal-docs-title"
      // Above the phone side menu (z-index 2000), which this can be opened from.
      sx={{ zIndex: 2100 }}
      PaperProps={{ className: "legal-docs-dialog" }}
    >
      <DialogTitle id="legal-docs-title" className="legal-docs-dialog__title">
        {p("title")}
        <IconButton aria-label={p("close")} onClick={onClose} className="legal-docs-dialog__close">
          <CloseRoundedIcon aria-hidden />
        </IconButton>
      </DialogTitle>
      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        variant="scrollable"
        allowScrollButtonsMobile
        className="legal-docs-dialog__tabs"
      >
        {TABS.map((t) => (
          <Tab key={t.key} value={t.key} label={p(t.label)} id={`legal-tab-${t.key}`} aria-controls="legal-docs-panel" />
        ))}
      </Tabs>
      <DialogContent
        ref={contentRef}
        dividers
        id="legal-docs-panel"
        role="tabpanel"
        aria-labelledby={`legal-tab-${tab}`}
        tabIndex={0}
        className="legal-docs-dialog__content"
      >
        {!i18n.language?.startsWith("en") && (
          <Typography className="legal-docs-dialog__lang-note">{p("english_only")}</Typography>
        )}
        {/* The documents are English legal text: keep them left-to-right in Hebrew too. */}
        <Box component="article" lang="en" dir="ltr" className="legal-docs-doc">
          <h2 className="legal-docs-doc__title">{heading.title}</h2>
          {heading.note && <p className="legal-docs-doc__note">{heading.note}</p>}
          {groupBlocks(doc.blocks).map((group, i) =>
            Array.isArray(group) ? (
              <ul key={i}>
                {group.map((item, j) => (
                  <li key={j}>{item.text}</li>
                ))}
              </ul>
            ) : group.type === "h" ? (
              <h3 key={i}>{group.text}</h3>
            ) : (
              <p key={i}>{group.text}</p>
            )
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
